import React, { useState } from "react";
import ExerciseFormSVG from "@/components/ExerciseFormSVG";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// `?v=` cache-buster bumped whenever we regenerate the anatomical illustration
// set. Doesn't affect API cost — just forces browsers to re-fetch the PNG.
const ILLUSTRATION_VERSION = "3";
const illustrationUrl = (exercise, bump) => {
  const suffix = `?v=${ILLUSTRATION_VERSION}${bump ? `&t=${bump}` : ""}`;
  if (exercise?.id) return `${BACKEND}/api/exercises/${exercise.id}/image${suffix}`;
  if (exercise?.name)
    return `${BACKEND}/api/exercises/image-by-name/${encodeURIComponent(exercise.name.trim())}${suffix}`;
  return null;
};

/**
 * Anatomical form tile for a curated exercise.
 *
 * Renders the Nano-Banana-generated anatomy chart (muscles highlighted in red)
 * for exercises that have one; falls back to the hand-drawn stick-figure SVG
 * from ExerciseFormSVG.jsx when the image is missing or fails to load.
 * The primary trained muscle drives the accent hue so the library reads at a
 * glance.
 */

// Primary muscle → subtle Tailwind accent. Muted enough to sit under the
// brand blaze without clashing. Kept dark to avoid the pastel "AI slop" look.
const MUSCLE_ACCENT = {
  chest: { ring: "border-rose-500/40", tint: "text-rose-300", bg: "bg-rose-500/8" },
  back: { ring: "border-indigo-500/40", tint: "text-indigo-300", bg: "bg-indigo-500/8" },
  lats: { ring: "border-indigo-500/40", tint: "text-indigo-300", bg: "bg-indigo-500/8" },
  shoulders: { ring: "border-sky-500/40", tint: "text-sky-300", bg: "bg-sky-500/8" },
  "front delts": { ring: "border-sky-500/40", tint: "text-sky-300", bg: "bg-sky-500/8" },
  biceps: { ring: "border-amber-500/40", tint: "text-amber-300", bg: "bg-amber-500/8" },
  triceps: { ring: "border-orange-500/40", tint: "text-orange-300", bg: "bg-orange-500/8" },
  quads: { ring: "border-emerald-500/40", tint: "text-emerald-300", bg: "bg-emerald-500/8" },
  glutes: { ring: "border-violet-500/40", tint: "text-violet-300", bg: "bg-violet-500/8" },
  hamstrings: { ring: "border-teal-500/40", tint: "text-teal-300", bg: "bg-teal-500/8" },
  core: { ring: "border-yellow-500/40", tint: "text-yellow-300", bg: "bg-yellow-500/8" },
  calves: { ring: "border-cyan-500/40", tint: "text-cyan-300", bg: "bg-cyan-500/8" },
};

const FALLBACK = { ring: "border-obsidian-600", tint: "text-blaze", bg: "bg-obsidian-900/60" };

export function accentFor(exerciseLike) {
  const muscles = exerciseLike?.muscles || [];
  const primary = (muscles[0] || "").toLowerCase();
  return (
    MUSCLE_ACCENT[primary] ||
    MUSCLE_ACCENT[Object.keys(MUSCLE_ACCENT).find((k) => primary.includes(k)) || ""] ||
    FALLBACK
  );
}

/** Internal — anatomical PNG first, SVG stick-figure fallback on error.
 * Resolves by id (curated/custom) or by NAME (routine/program exercises). */
function IllustrationOrSVG({ exercise, svgClass, imgClass, bump }) {
  const [failed, setFailed] = useState(false);
  const src = illustrationUrl(exercise, bump);
  if (!src || failed) {
    return <ExerciseFormSVG exercise={exercise} className={svgClass} />;
  }
  return (
    <img
      src={src}
      alt={exercise.name || "exercise illustration"}
      loading="lazy"
      onError={() => setFailed(true)}
      className={imgClass}
    />
  );
}

/**
 * Compact anatomical tile. Sizes:
 *   sm   = 40px (thumbnails in workout history / search suggestions)
 *   md   = 48px
 *   hero = full-bleed 176px (library cards)
 *   any  = 288px (detail modal)
 */
export default function ExerciseIcon({ exercise, size = "md", className = "", bump }) {
  const { ring, tint, bg } = accentFor(exercise || {});

  if (size === "sm") {
    return (
      <div
        className={`w-10 h-10 rounded-sm border ${ring} bg-white flex items-center justify-center shrink-0 overflow-hidden ${className}`}
        aria-hidden="true"
      >
        <IllustrationOrSVG
          exercise={exercise}
          bump={bump}
          svgClass={`w-8 h-8 ${tint}`}
          imgClass="max-w-full max-h-full object-contain object-center"
        />
      </div>
    );
  }
  if (size === "md") {
    return (
      <div
        className={`w-12 h-12 rounded-sm border ${ring} bg-white flex items-center justify-center shrink-0 overflow-hidden ${className}`}
        aria-hidden="true"
      >
        <IllustrationOrSVG
          exercise={exercise}
          bump={bump}
          svgClass={`w-10 h-10 ${tint}`}
          imgClass="max-w-full max-h-full object-contain object-center"
        />
      </div>
    );
  }
  // hero (cards) and detail modal — flex-centering is more reliable than
  // `absolute inset-2 + grid place-items-center` when the image aspect
  // ratio drifts from the container. Keep a tight inner max so the render
  // never crops or drifts to a corner.
  const heroHeight = size === "hero" ? "h-44" : "h-72";
  return (
    <div
      className={`relative w-full ${heroHeight} border-b ${ring} bg-white overflow-hidden flex items-center justify-center ${className}`}
      aria-hidden="true"
    >
      <IllustrationOrSVG
        exercise={exercise}
        bump={bump}
        svgClass={`max-w-[92%] max-h-[92%] ${tint}`}
        imgClass="max-w-[92%] max-h-[92%] object-contain object-center block"
      />
    </div>
  );
}

export function MuscleChip({ muscle }) {
  const accent = MUSCLE_ACCENT[muscle?.toLowerCase()] || FALLBACK;
  return (
    <span
      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-full ${accent.ring} ${accent.tint} ${accent.bg}`}
    >
      {muscle}
    </span>
  );
}
